import React from 'react';
import { FaExclamationTriangle, FaTimes, FaCalendarAlt, FaClock, FaCheckCircle } from 'react-icons/fa';

const DeleteScheduleModal = ({ 
  isOpen, 
  schedule, 
  jobs, 
  onConfirm, 
  onCancel, 
  loading, 
  error 
}) => {
  if (!isOpen || !schedule) return null;

  const formatJobDate = (jobDate) => {
    if (!jobDate || !jobDate._seconds) return 'N/A';
    const date = new Date(jobDate._seconds * 1000);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Group jobs by status for better display
  const jobsByStatus = jobs.reduce((acc, job) => {
    const status = job.status || 'unknown';
    if (!acc[status]) acc[status] = [];
    acc[status].push(job);
    return acc;
  }, {});

  const getStatusColor = (status) => {
    const colors = {
      'pending-quote': 'text-yellow-700',
      'quote-received': 'text-blue-700',
      'pending-approval': 'text-orange-700',
      'approved': 'text-green-700',
      'in-progress': 'text-cyan-700',
      'service-delayed': 'text-red-700'
    };
    return colors[status] || 'text-gray-700';
  };

  const getStatusLabel = (status) => {
    return status?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <FaExclamationTriangle className="mr-3 text-2xl" />
            <h2 className="text-xl font-bold">Delete Job Schedule</h2>
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-white hover:text-red-200 transition-colors"
            aria-label="Close"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Schedule Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2">Schedule Details</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p><span className="font-medium">Franchisee:</span> {schedule.franchiseeInfo?.franchiseName || 'N/A'}</p>
              <p><span className="font-medium">Client:</span> {schedule.clientInfo?.clientName || 'N/A'}</p>
              <p><span className="font-medium">Household:</span> {`${schedule.householdInfo?.firstName || ''} ${schedule.householdInfo?.lastName || ''}`.trim() || 'N/A'}</p>
            </div>
          </div>

          {/* Warning Message */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <div className="flex items-start">
              <FaExclamationTriangle className="text-red-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-800 mb-1">
                  This action cannot be undone!
                </p>
                <p className="text-sm text-red-700">
                  Deleting this schedule will permanently mark it and all associated jobs as deleted.
                  This cannot be reversed.
                </p>
              </div>
            </div>
          </div>

          {/* Jobs List */}
          {jobs.length > 0 ? (
            <div className="mb-4">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <FaCalendarAlt className="mr-2 text-red-600" />
                The following {jobs.length} job(s) will be deleted:
              </h3>
              
              {/* Group jobs by status */}
              {Object.entries(jobsByStatus).map(([status, statusJobs]) => (
                <div key={status} className="mb-3">
                  <h4 className={`text-sm font-medium ${getStatusColor(status)} mb-2`}>
                    {getStatusLabel(status)} ({statusJobs.length})
                  </h4>
                  <ul className="space-y-2">
                    {statusJobs.map(job => (
                      <li 
                        key={job.id} 
                        className="bg-gray-50 rounded px-3 py-2 text-sm border border-gray-200 flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <FaCalendarAlt className="mr-2 text-gray-400 flex-shrink-0" />
                          <span className="font-medium">{formatJobDate(job.jobDate)}</span>
                          {job.jobTime && (
                            <>
                              <FaClock className="ml-3 mr-1 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-600">{job.jobTime}</span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">ID: {job.id.substring(0, 8)}...</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
              <div className="flex items-center">
                <FaCheckCircle className="text-blue-500 mr-2" />
                <p className="text-sm text-blue-800">
                  No active jobs found for this schedule.
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-medium">Error:</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Deleting...
              </>
            ) : (
              'Delete Schedule & Jobs'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteScheduleModal;
